import { SignalCategory } from '@firmable/contracts';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useFilter } from '../Filter';

export type State = {
  pageIndex: Record<SignalCategory, number>;
  pageSize: Record<SignalCategory, number>;
  sorting: Record<SignalCategory, Record<string, 'asc' | 'desc'>>;
  search: Record<SignalCategory, Record<string, string>>;
};

export type Action = {
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: (updater: MRT_SortingState) => void;
  setSearch: (filter: MRT_ColumnFiltersState) => void;
  setSearchKeyAcrossCategories: (key: string, value: string | null) => void;
  clearSearch: () => void;
  reset: () => void;
};

const initialPageIndex: State['pageIndex'] = { company: 0, people: 0, intent: 0 };
const initialSorting: State['sorting'] = { company: {}, people: {}, intent: {} };
const initialSearch: State['search'] = { company: {}, people: {}, intent: {} };

/**
 * Store to maintain state of search list for both Company and People
 */
const use = create<State & Action>()(
  devtools(
    immer((set) => ({
      pageIndex: { ...initialPageIndex },
      pageSize: {
        company: 10,
        people: 10,
        intent: 10,
      },
      sorting: {
        company: { ...initialSorting.company },
        people: { ...initialSorting.people },
        intent: { ...initialSorting.intent },
      },
      search: {
        company: { ...initialSearch.company },
        people: { ...initialSearch.people },
        intent: { ...initialSearch.intent },
      },
      setPageIndex: (pageIndex: number) =>
        set(
          (state) => {
            const { category } = useFilter.getState();
            state.pageIndex[category] = pageIndex;
            // reset the people sorting when company page index changes
            if (category === 'company') {
              state.pageIndex.people = 0;
            }
          },
          false,
          { type: 'setPageIndex' },
        ),
      setPageSize: (pageSize: number) =>
        set(
          (state) => {
            const { category } = useFilter.getState();
            state.pageSize[category] = pageSize;
          },
          false,
          { type: 'setPageSize' },
        ),
      setSorting: (sortingState: MRT_SortingState) =>
        set(
          (state: State) => {
            const { category } = useFilter.getState();
            state.sorting[category] = sortingState.reduce(
              (acc, { id, desc }) => ({
                ...acc,
                [id === 'company-name' || id === 'people-name' ? 'name' : id]: desc ? 'desc' : 'asc',
                //[id]: desc ? 'desc' : 'asc',
              }),
              {},
            );
          },
          false,
          { type: 'setSorting' },
        ),
      setSearch: (filterState: MRT_ColumnFiltersState) =>
        set(
          (state: State) => {
            const { category } = useFilter.getState();
            state.search[category] = filterState.reduce(
              (acc, { id, value }) => ({
                ...acc,
                [id === 'company-name' || id === 'people-name' ? 'name' : id]: value as string,
                //[id]: value as string,
              }),
              {} as Record<string, string>,
            );
          },
          false,
          { type: 'setFilter' },
        ),
      setSearchKeyAcrossCategories: (key: string, value: string | null) =>
        set(
          (state: State) => {
            (['company', 'people', 'intent'] as SignalCategory[]).forEach((cat) => {
              if (value === null) {
                delete state.search[cat][key];
              } else {
                state.search[cat][key] = value;
              }
            });
          },
          false,
          { type: 'setSearchKeyAcrossCategories' },
        ),
      clearSearch: () =>
        set(
          (state) => {
            state.search = {
              company: { ...initialSearch.company },
              people: { ...initialSearch.people },
              intent: { ...initialSearch.intent },
            };
          },
          false,
          { type: 'clearSearch' },
        ),
      reset: () =>
        set(
          (state) => {
            state.pageIndex = { ...initialPageIndex };
            state.sorting = {
              company: { ...initialSorting.company },
              people: { ...initialSorting.people },
              intent: { ...initialSorting.intent },
            };
            state.search = {
              company: { ...initialSearch.company },
              people: { ...initialSearch.people },
              intent: { ...initialSearch.intent },
            };
          },
          false,
          { type: 'RESET' },
        ),
    })),
    {
      store: 'SignalListRecordsCompany',
    },
  ),
);

export { use };
